import { StringContext } from "../../../core/StringContext";
import { StringModule } from "../../../core/StringModule";
import { StringObject } from "../../../objects/StringObject";
import {
  StringDevtoolDefinition,
  StringDevtoolHotkey,
  StringDevtoolProvider,
  StringDevtoolState,
} from "../../../models/devtools/StringDevtool";
import { resolveDevtoolsIcon } from "./StringDevIconRegistry";
import { StringDevOverlayRegistry } from "./StringDevOverlayRegistry";
import { ensureStringDevtoolsSharedStyles } from "./StringDevStyleSystem";
import { StringDevViewportLayer } from "./StringDevViewportLayer";

export interface StringDevModuleDefinitionConfig {
  id: string;
  label: string;
  icon: string;
  order?: number;
  group?: number;
  hotkey?: StringDevtoolHotkey;
}

export interface StringDevConnectsConfig {
  /** If true, connects to every StringObject (overlay default). If false, only explicit matches below + dev-inspect fallback. */
  global?: boolean;
  /** Connect to objects whose string="…" token list includes any of these keys. */
  keys?: string[];
  /** Connect to objects whose DOM element has any of these attributes present. */
  attributes?: string[];
}

export interface StringDevOverlayConfig {
  /** Unique layer name, e.g. "devtools-progress" */
  layerName: string;
  /** Z-index for the viewport layer */
  zIndex: number;
  /** Data-attribute added to the layer root, e.g. "data-string-progress-layer" */
  layerAttribute?: string;
  /** Overlay ID used for the anchor/collision system. Omit if not needed. */
  overlayId?: string;
  /** Whether the overlay starts enabled before dock preferences apply. Default: false */
  defaultEnabled?: boolean;
  /** For badge overlay modules: data-attribute placed on each badge element */
  badgeAttribute?: string;
}

export interface StringDevModuleConfig extends StringDevModuleDefinitionConfig {
  /** Declare CSS styles to inject once. Can be a string or a lazy getter. */
  styles?: string | (() => string);
  /** Overlay layer configuration. Declare for any module that uses a viewport layer. */
  overlay?: StringDevOverlayConfig;
  /** Declare which StringObjects this module connects to. Replaces htmlKey assignment in constructor. */
  connects?: StringDevConnectsConfig;
}

export class StringDevModule extends StringModule implements StringDevtoolProvider {
  /** Declare module metadata statically. Subclasses override this. */
  static devtool: StringDevModuleConfig | null = null;

  protected readonly overlayRegistry = StringDevOverlayRegistry.getInstance();

  private readonly acquiredViewportLayers = new Map<string, StringDevViewportLayer>();
  private readonly devtoolListeners = new Set<(state: StringDevtoolState) => void>();
  private hotkeyHandler: ((event: KeyboardEvent) => void) | null = null;
  protected devtoolConfig: StringDevModuleDefinitionConfig | null = null;

  constructor(context: StringContext) {
    super(context);
    this._type = 2;
    ensureStringDevtoolsSharedStyles();

    const staticConfig = (this.constructor as typeof StringDevModule).devtool;
    if (staticConfig) {
      this.configureDevtool(staticConfig);
      this.bindDevtoolHotkey(staticConfig.hotkey);
    }

    const rawStaticStyles = staticConfig?.styles;
    const styles =
      (typeof rawStaticStyles === "function" ? rawStaticStyles() : rawStaticStyles) ??
      this.getStyles();
    if (styles) {
      this.ensureStyle(`${this.getStyleScopeId(styles)}-styles`, styles);
    }
  }

  protected override get respectSelfDisable(): boolean {
    return false;
  }

  protected get connectsConfig(): StringDevConnectsConfig | undefined {
    return (this.constructor as typeof StringDevModule).devtool?.connects;
  }

  canConnect(object: StringObject): boolean {
    const connects = this.connectsConfig;
    if (!connects) {
      // No connects declared — fall back to base htmlKey matching.
      return super.canConnect(object);
    }

    if (connects.global === true) {
      return true;
    }

    // global: false (or omitted) — match explicit keys/attributes, plus dev-inspect fallback.
    if (object.keys.includes("dev-inspect")) {
      return true;
    }
    if (connects.keys?.some((k) => object.keys.includes(k))) {
      return true;
    }
    if (connects.attributes?.some((attr) => object.htmlElement.hasAttribute(attr))) {
      return true;
    }

    return false;
  }

  protected getStyleScopeId(stylesText: string): string {
    const firstKey =
      this.htmlKey || (this.constructor as typeof StringDevModule).devtool?.connects?.keys?.[0];
    if (firstKey) {
      return firstKey;
    }

    let hash = 0;
    for (let i = 0; i < stylesText.length; i += 1) {
      hash = (hash * 31 + stylesText.charCodeAt(i)) >>> 0;
    }

    return `string-dev-${hash.toString(16)}`;
  }

  protected getStyles(): string | null {
    return null;
  }

  public getDevtoolDefinition(): StringDevtoolDefinition | null {
    if (!this.devtoolConfig) {
      return null;
    }

    const config = this.devtoolConfig;
    const subBadges = this.getDevtoolSubBadges();
    return {
      id: config.id,
      label: config.label,
      icon: config.icon,
      order: config.order,
      group: config.group,
      hotkey: config.hotkey,
      subBadges: subBadges.length > 0 ? subBadges : undefined,
      getState: () => ({ active: this.getDevtoolActiveState() }),
      setActive: (active) => {
        this.setDevtoolActiveState(active);
      },
      subscribe: (listener) => {
        this.devtoolListeners.add(listener);
        listener({ active: this.getDevtoolActiveState() });
        return () => {
          this.devtoolListeners.delete(listener);
        };
      },
    };
  }

  protected getDevtoolSubBadges(): import("../../../models/devtools/StringDevtool").StringDevtoolSubBadge[] {
    return [];
  }

  protected configureDevtool(config: StringDevModuleDefinitionConfig): void {
    this.devtoolConfig = config;
  }

  protected bindDevtoolHotkey(hotkey?: StringDevtoolHotkey): void {
    if (typeof window === "undefined" || !hotkey) {
      return;
    }

    this.hotkeyHandler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target.isContentEditable)
      ) {
        return;
      }

      if (
        event.key.toLowerCase() !== hotkey.key.toLowerCase() ||
        event.shiftKey !== (hotkey.shiftKey ?? false) ||
        event.ctrlKey !== (hotkey.ctrlKey ?? false) ||
        event.altKey !== (hotkey.altKey ?? false) ||
        event.metaKey !== (hotkey.metaKey ?? false)
      ) {
        return;
      }

      event.preventDefault();
      this.setDevtoolActiveState(!this.getDevtoolActiveState());
    };

    window.addEventListener("keydown", this.hotkeyHandler);
  }

  protected emitDevtoolState(active: boolean = this.getDevtoolActiveState()): void {
    const state = { active };
    for (const listener of this.devtoolListeners) {
      listener(state);
    }
  }

  protected acquireViewportLayer(id: string, zIndex: number): StringDevViewportLayer {
    const existing = this.acquiredViewportLayers.get(id);
    if (existing) {
      return existing;
    }

    const layer = this.overlayRegistry.acquire(id, zIndex);
    this.acquiredViewportLayers.set(id, layer);
    return layer;
  }

  protected releaseViewportLayer(id: string): void {
    if (!this.acquiredViewportLayers.has(id)) {
      return;
    }

    this.overlayRegistry.release(id);
    this.acquiredViewportLayers.delete(id);
  }

  protected ensureStyle(styleId: string, cssText: string): HTMLStyleElement {
    const existing = document.getElementById(styleId);
    if (existing instanceof HTMLStyleElement) {
      return existing;
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = cssText;
    document.head.appendChild(style);
    return style;
  }

  protected getWorldHost(): HTMLElement {
    return this.data.scroll.container ?? document.body ?? document.documentElement;
  }

  protected getDevtoolActiveState(): boolean {
    return false;
  }

  protected setDevtoolActiveState(_active: boolean): void {}

  destroy(): void {
    if (this.hotkeyHandler) {
      window.removeEventListener("keydown", this.hotkeyHandler);
      this.hotkeyHandler = null;
    }
    this.devtoolListeners.clear();
    for (const id of this.acquiredViewportLayers.keys()) {
      this.overlayRegistry.release(id);
    }
    this.acquiredViewportLayers.clear();
    super.destroy();
  }
}
